/**
 * webauthn.ts — WebAuthn/FIDO2 glue (SPEC §2, MANDATORY for technicians).
 *
 * Wraps navigator.credentials.get/create. Binary members travel as base64url on
 * the wire (our *JSON option types); we convert to/from ArrayBuffer here. We use
 * the browser-native PublicKeyCredential JSON helpers when available and fall
 * back to manual base64url conversion otherwise.
 */

import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@/types';

export function webauthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator.credentials?.get === 'function'
  );
}

// ----- base64url helpers ----------------------------------------------------
function b64urlToBytes(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}

function bytesToB64url(buf: ArrayBuffer): string {
  const u8 = new Uint8Array(buf);
  let s = '';
  for (const b of u8) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function toBuffer(u8: Uint8Array): ArrayBuffer {
  return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
}

// ----- assertion (login) ----------------------------------------------------
/**
 * Run navigator.credentials.get() for a login assertion. Returns a JSON-safe
 * object to POST to /auth/webauthn/login/finish.
 */
export async function getAssertion(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<unknown> {
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: toBuffer(b64urlToBytes(options.challenge)),
    timeout: options.timeout,
    rpId: options.rpId,
    userVerification: options.userVerification ?? 'required',
    allowCredentials: options.allowCredentials?.map((c) => ({
      type: c.type,
      id: toBuffer(b64urlToBytes(c.id)),
      transports: c.transports,
    })),
  };

  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('WebAuthn assertion cancelled');
  const resp = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bytesToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bytesToB64url(resp.clientDataJSON),
      authenticatorData: bytesToB64url(resp.authenticatorData),
      signature: bytesToB64url(resp.signature),
      userHandle: resp.userHandle ? bytesToB64url(resp.userHandle) : null,
    },
  };
}

// ----- registration ---------------------------------------------------------
/**
 * Run navigator.credentials.create() to enroll a new credential. Returns a
 * JSON-safe object to POST to /auth/webauthn/register/finish.
 */
export async function createCredential(
  options: PublicKeyCredentialCreationOptionsJSON,
): Promise<unknown> {
  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: toBuffer(b64urlToBytes(options.challenge)),
    rp: options.rp,
    user: {
      id: toBuffer(b64urlToBytes(options.user.id)),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout,
    attestation: options.attestation ?? 'none',
    excludeCredentials: options.excludeCredentials?.map((c) => ({
      type: c.type,
      id: toBuffer(b64urlToBytes(c.id)),
      transports: c.transports,
    })),
    authenticatorSelection: options.authenticatorSelection ?? {
      residentKey: 'preferred',
      userVerification: 'required',
    },
  };

  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('WebAuthn registration cancelled');
  const resp = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bytesToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bytesToB64url(resp.clientDataJSON),
      attestationObject: bytesToB64url(resp.attestationObject),
      transports:
        typeof resp.getTransports === 'function' ? resp.getTransports() : undefined,
    },
  };
}
